import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { errorType } from "../../shared/ui/toast/errors/toast/models/ToastProps";
import {
	AppThunkError,
	ErrorModelState,
	ErrorState,
	ErrorStateWithoutd,
	InitialStateErrors,
} from "./ErrorModelState";

const initialState: InitialStateErrors = {
	errors: [],
};

// Store pending errors to be added with debouncing
let pendingErrorTimeout: NodeJS.Timeout | null = null;
const DEBOUNCE_DELAY = 100; // milliseconds

export const errorSlice = createSlice({
	name: "errors",
	initialState,
	reducers: {
		addError: (state, action: PayloadAction<ErrorStateWithoutd>) => {
			state.errors.push(getErrorObj(action.payload));
		},
		setAddSuccessMessage: (state, action: PayloadAction<ErrorState>) => {
			state.errors.push(action.payload);
		},
		addWarningMessage: (state, action: PayloadAction<ErrorStateWithoutd>) => {
			state.errors.push(getErrorObj(action.payload, "warning"));
		},
		setAddErrorMessage: (state, action: PayloadAction<ErrorState>) => {
			state.errors.push(action.payload);
		},
		addUnauthorizedErrorMessage: (
			state,
			action: PayloadAction<ErrorStateWithoutd>
		) => {
			if (!state.errors.find((el) => el.type === "unauthorized"))
				state.errors.push(getErrorObj(action.payload, "unauthorized"));
		},
		resetErrors: (state) => {
			state.errors = [];
		},
		markErrorRemoving: (state, action: PayloadAction<number | undefined>) => {
			const error = state.errors.find((el) => el.id === action.payload);
			if (error) {
				error.removing = true;
			}
		},
		removeError: (state, action: PayloadAction<number | undefined>) => {
			state.errors = state.errors.filter((el) => el.id !== action.payload);
		},
	},
});

export const {
	addError,
	resetErrors,
	removeError,
	markErrorRemoving,
	setAddSuccessMessage,
	setAddErrorMessage,
	addWarningMessage,
	addUnauthorizedErrorMessage,
} = errorSlice.actions;

export const getErrorObj = (error: ErrorStateWithoutd, type?: errorType) => {
	const id: number = Date.now() * Math.round(Math.random() * 100);
	return { ...error, id, type };
};

export const addSuccessMessage =
	(error: ErrorStateWithoutd): AppThunkError =>
	(dispatch) => {
		const obj: ErrorState = getErrorObj(error, "success");
		dispatch(setAddSuccessMessage(obj));
		setTimeout(() => {
			dispatch(markErrorRemoving(obj.id));
			setTimeout(() => {
				dispatch(removeError(obj.id));
			}, 300); // Wait for animation to complete
		}, 2000);
	};
export const addErrorMessage =
	(error: ErrorStateWithoutd): AppThunkError =>
	(dispatch, getState) => {
		// Check if a similar error already exists (same title and body)
		const existingErrors = getState().errors.errors;
		const isDuplicate = existingErrors.some(
			(existingError) =>
				existingError.title === error.title &&
				existingError.body === error.body &&
				existingError.type === "error"
		);

		// If duplicate found, don't add another toast
		if (isDuplicate) {
			return;
		}

		// Debounce: clear previous timeout if exists
		if (pendingErrorTimeout) {
			clearTimeout(pendingErrorTimeout);
		}

		// Add error after a short delay to batch multiple errors
		pendingErrorTimeout = setTimeout(() => {
			const obj: ErrorState = getErrorObj(error, "error");
			dispatch(setAddErrorMessage(obj));
			setTimeout(() => {
				dispatch(markErrorRemoving(obj.id));
				setTimeout(() => {
					dispatch(removeError(obj.id));
				}, 300); // Wait for animation to complete
			}, 2000);
		}, DEBOUNCE_DELAY);
	};

export const selectErrors = (state: ErrorModelState) => state.errors.errors;

export default errorSlice.reducer;
